import { Card, CardContent, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import React from 'react';

import { SeamStatus } from 'app/constants/seam-status';
import { PriorityEnumDict } from 'app/enum';
import { CrisRequest, SeamRequest } from 'app/model';
import { Formatters } from 'app/utils/formatters';

import { RequestStatus } from './request-status';
import { Translate } from './translate';
import { GeneralConstants } from '../constants/general-constants';
import { Strings } from '../utils/strings';

interface RequestTableProps<T extends CrisRequest | SeamRequest> {
    requests: T[];

    cardClass?: string;

    onSelect: (request: T) => void;
}

export class RequestCards<T extends CrisRequest | SeamRequest> extends React.Component<RequestTableProps<T>> {
    public render(): React.ReactNode {
        return this.props.requests.map((request, index) => (
            <Card
                elevation={3}
                onClick={() => this.props.onSelect(request)}
                className={this.props.cardClass}
                style={{ border: 'lightgray 1px solid' }}
                key={index}
            >
                <CardContent>
                    {request.dueDate && (
                        <Typography variant='subtitle2' className='pull-right'>
                            {format(parseISO(request.dueDate), 'MM/dd/yyyy')}
                        </Typography>
                    )}
                    <Typography variant='h5' component='h2' className='text-nowrap ellipsis'>
                        {RequestCards.getStoreName(request)}&nbsp;
                    </Typography>
                    <Typography color='textSecondary'>
                        <Translate tkey={`headings:${RequestCards.getRequestType(request)}`} />
                        &nbsp;
                        {RequestCards.getRequestId(request).substr(0, GeneralConstants.SHORT_ID_LENGTH)}
                    </Typography>
                    <Typography variant='body2' className='pull-right'>
                        {Formatters.userDisplayName(request.owner)}
                        {RequestCards.getTaggerPriority(request)}
                    </Typography>
                    <RequestStatus status={request.currentPlace} />
                </CardContent>
            </Card>
        ));
    }

    private static getRequestType(request: CrisRequest | SeamRequest): string {
        if (RequestCards.isCrisRequest(request)) {
            return 'CRIS';
        }

        return Strings.seamRequestType(request);
    }

    private static getTaggerPriority(request: CrisRequest | SeamRequest): string | undefined {
        if (
            RequestCards.isCrisRequest(request) ||
            request.currentPlace !== SeamStatus.ASSIGN ||
            request.crisRequest.taggerPriority === undefined
        ) {
            return '';
        }

        return PriorityEnumDict[request.crisRequest.taggerPriority];
    }

    private static getStoreName(request: CrisRequest | SeamRequest): string | undefined {
        if (RequestCards.isCrisRequest(request)) {
            return request.brand?.name || request.elementName || '[Unspecified]';
        }

        return request.store.name;
    }

    private static isCrisRequest(request: CrisRequest | SeamRequest): request is CrisRequest {
        return (request as SeamRequest).crisRequest === undefined;
    }

    private static getRequestId(request: CrisRequest | SeamRequest): string {
        if (RequestCards.isCrisRequest(request)) {
            return request.id;
        }

        return request.crisRequest.id;
    }
}
